import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

import DrawerSteps from "./components/DrawerSteps";
import {
  isLingerie,
  isFootwear,
  isChildren,
  isSpecificFlow,
} from "../../utils/categoryParams";
import {
  emptyLingerieAnswers,
  buildLingeriePayload,
  LingerieAnswers,
} from "../../constants/lingerieFlow";

import {
  MODAL_STEPS,
  ERROR_EMPTY,
  GENDERS,
  VALIDATION_RULES,
  BASE_SIZES,
  LANGUAGE_RULES,
} from "../../constants/modal";

import {
  convertFeetAndInchesToCm,
  convertHeightToFeetAndInches,
  getErrorTitle,
  getLangBase,
  getStepMaps,
  getSubdomainPrefixUpper,
  INCHtoCM,
  initializeLanguage,
  LBStoKG,
  useIsMobile,
} from "../../utils";
import { useUserContext } from "../../store/userContext";
import "../../i18n";

import {
  checkStocks,
  questionPushTrigger,
  recommend,
  specificRecommend,
} from "../../api/endpoints";
import { Box } from "@mui/material";

import { logger } from "../../utils/logging";

import {
  font,
  getQueryParam,
  resultScreenUI,
  titleStyles,
  uxGender,
  uxRoute,
  uxRouteNone,
  uxRouteDefault,
  uxRouteQuestionOnly,
  uxRouteQuestionPushScan,
} from "../../configs";
import { i18n, loadTranslations } from "../../i18n";
import {
  RecommendedSizeType,
  ReducedResultType,
  ShoeRecommendationType,
  SimilarProductType,
} from "../../types/result";
import {
  getSavedLocalStorage,
  removeLocalStore,
} from "../../store/localStoreUtils";
import { findBrandByDomain, findDomainData } from "../../configs/configLoader";
import {
  getStepByNumber,
  handleAnalytics,
  handleTrackingStep,
} from "../../utils/tracking";

import { postCloseIframe, postGetSizes } from "../../utils/post";
import { handleStep } from "../../utils/steps";
import {
  MOCK_ADULT_RECOMMENDATION,
  MOCK_SIMILAR_PRODUCTS,
  MOCK_SIMILAR_PRODUCTS_Devernois,
  MOCK_SIMILAR_PRODUCTS_Galore,
  MOCK_ADULT_STOCKS_DATA,
  MOCK_FOOTWEAR_RECOMMENDATION,
  MOCK_FOOTWEAR_STOCKS_DATA,
} from "../../constants/sizes";

import SrpTitleGuide from "./components/SrpTitleGuide";
import VictoriaBeckhamGuide from "./components/VictoriaBeckhamGuide";
import BackArrowButton from "./components/BackArrowButton";
import CloseIconButton from "./components/CloseIconButton";
import StepTitle from "./components/StepTitle";

import { usePopupStyles } from "../../hooks/usePopupStyles";

import "./index.css";

const DrawerModal = () => {
  const isMobile = useIsMobile();

  const userContext = useUserContext() || undefined;

  const shoeSelectedSize = useUserContext()?.selectedSize;

  const updateSelectedSize = useUserContext()?.updateSelectedSize;

  const [mid, setMid] = useState<string | null>(userContext?.mid || null);
  const [uid, setUid] = useState<string | null>(userContext?.uid || null);
  const [pid, setPid] = useState<string | null>(userContext?.pid || null);

  const { t } = useTranslation("components/intro");

  const localGender = getSavedLocalStorage("gender") || "";

  const [selectedGender, setSelectedGender] = useState(
    !uxGender ? "" : uxGender === "male" ? GENDERS.M : GENDERS.F,
  );

  const [selectedShoeGender, setSelectedShoeGender] = useState<string | null>(
    !uxGender ? "" : uxGender === "male" ? GENDERS.M : GENDERS.F,
  );

  const [selectedRoute, setSelectedRoute] = useState(uxRoute || "");

  const [shoeQuestion, setShoeQuestion] = useState<string | null>(null);

  const [lingerieAnswers, setLingerieAnswers] = useState<LingerieAnswers>(() =>
    emptyLingerieAnswers(),
  );

  const setLingerieAnswer = (answer: Partial<LingerieAnswers>) => {
    setLingerieAnswers((prev) => ({ ...prev, ...answer }) as LingerieAnswers);
  };

  const urlParameters = new URLSearchParams(window.location.search);

  const domain = urlParameters.get("domain");
  const lang = urlParameters.get("lang");
  const mock = urlParameters.get("mock") === "true";
  const mockSimilarProducts =
    urlParameters.get("mockSimilarProducts") === "true";

  const OUT_OF_RANGE_TITLE = t("steps.error_outOfRange");

  const [recommendedSize, setRecommendedSize] = useState<
    RecommendedSizeType[] | null
  >();
  const [reducedResult, setReducedResult] =
    useState<ReducedResultType | null>();
  const [needValidate, setNeedValidate] = useState(false);
  const [productStockData, setProductStockData] = useState<any | null>(null);
  const [variantId, setVariantId] = useState<string | null>(null);
  const [variantCol, setVariantCol] = useState<string | null>(null);
  const stocksRetrieved = useRef(false);
  const [similarProducts, setSimilarProducts] = useState<
    SimilarProductType[] | null
  >(null);
  const [isSizeUnavailable, setIsSizeUnavailable] = useState<boolean>(false);
  const [selectedSize, setSelectedSize] = useState<RecommendedSizeType | null>(
    null,
  );
  const [isTranslationsLoaded, setIsTranslationsLoaded] = useState(false);
  const [isScanError, setIsScanError] = useState(false);
  const [policyChecked, setPolicyChecked] = useState(false);
  const [touchedCompany, setTouchedCompany] = useState(false);
  const [feetTouched, setFeetTouched] = useState(false);
  const [inchesTouched, setInchesTouched] = useState(false);

  const outOfRange = useRef<boolean>(false);

  const brandDefined = findBrandByDomain();

  const isLacoste = brandDefined?.name === "Lacoste";
  const isVelour = brandDefined?.name === "Velour Garments";
  const isSRP = brandDefined?.name === "SRP";
  const isGivenchy = brandDefined?.name === "Givenchy";
  const isBlackstore = brandDefined?.name === "Blackstore";
  const isDevernois = brandDefined?.name === "Devernois";
  const isGalore = brandDefined?.name === "Galore";
  const isVB = brandDefined?.name === "Victoria Beckham";
  const isNinaRicci = brandDefined?.name === "Nina Ricci";
  const isAntonelle = brandDefined?.name === "Antonelle";
  const isAsphalte = brandDefined?.name === "Asphalte";
  const isSAAJ = brandDefined?.name === "SAAJ";
  const isMarcyParis = brandDefined?.name === "Marcy Paris";

  const MODAL_STEPS_TITLE = [
    t("steps.consent"),
    t("steps.gender"),
    t("steps.intro_mobile"),
    t("steps.intro"),
    t(isFootwear ? "steps.device_select_shoe" : "steps.device_select"),
    t("steps.shoe_gender"),
    t("steps.shoe_sizes"),
    t("steps.shoe_questions"),
    t(isFootwear ? "steps.qr_code_shoe" : "steps.qr_code"),
    t("steps.belly"),
    t(selectedGender === GENDERS.M ? "steps.torso.male" : "steps.torso.female"),
    t("steps.cuisses"),
    t(isFootwear ? "steps.shoe_sizes" : "steps.breasts"),
    t("steps.sleeves"),
    t("steps.lingerie_question_1"),
    t("steps.lingerie_question_2"),
    t("steps.lingerie_question_3"),
    t("steps.lingerie_question_4"),
    t(getErrorTitle({ outOfRange: outOfRange.current })),
    t(isFootwear ? "steps.result_shoe" : "steps.result"),
    t("steps.unavailable"),
    t("steps.antibracketing"),
    t("steps.lingerie_question_5"),
    t("steps.lingerie_question_6"),
  ];

  const domainData = findDomainData();

  const [previousStep, setPreviousStep] = useState<{
    number: number;
  } | null>(null);

  const [unit, setUnit] = useState(
    domainData?.unitSystem === "imperial" ? "feet" : "cm",
  );
  const [height, setHeight] = useState("");
  const [feet, setFeet] = useState("");
  const [inches, setInches] = useState("");

  const [weight, setWeight] = useState<string | undefined>("");
  const [unitWeight, setUnitWeight] = useState(
    domainData?.unitSystem === "imperial" ? "lbs" : "kg",
  );

  const [unitAge, setUnitAge] = useState("year");
  const [age, setAge] = useState("");
  const [company, setCompany] = useState("");
  const [ageChildrenYears, setAgeChildrenYears] = useState("");
  const [ageChildrenMonths, setAgeChildrenMonths] = useState("");

  const [sizeCountry, setSizeCountry] = useState<string>("");

  const [disableContinue, setDisableContinue] = useState(false);
  const [error, setError] = useState(ERROR_EMPTY);

  const [step, setStep] = useState<{ number: number } | null>(null);

  const [defaultStep, setDefaultStep] = useState<{ number: number } | null>(
    null,
  );

  const bellyValue = useRef<number>(0);
  const torsoValue = useRef<number>(0);
  const cuissesValue = useRef<number>(0);
  const sleeveValue = useRef<number | undefined>();
  const sizeValue = useRef<string | null>("");
  const cupValue = useRef<string | null>("");

  const popupRef = useRef<HTMLDivElement | null>(null);

  const resultLoading = useRef<boolean>(false);
  const isLingerieRestarting = useRef<boolean>(false);

  const {
    titlesStyles,
    descriptionStyles,
    titleTextTransform,
    headerStyles,
    popupStyles,
  } = usePopupStyles({
    font,
    titleStyles,
    step,
    lang,
  });

  // Focus trap: auto-focus popup on mount and trap focus inside
  useEffect(() => {
    if (popupRef.current) {
      popupRef.current.focus();
    }
  }, []);

  useEffect(() => {
    const popup = popupRef.current;
    if (!popup) return;

    const handleFocusTrap = (e: KeyboardEvent) => {
      if (e.key !== "Tab") return;

      const focusableElements = popup.querySelectorAll<HTMLElement>(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      if (focusableElements.length === 0) return;

      const first = focusableElements[0];
      const last = focusableElements[focusableElements.length - 1];

      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    popup.addEventListener("keydown", handleFocusTrap);
    return () => popup.removeEventListener("keydown", handleFocusTrap);
  }, [step]);

  useEffect(() => {
    handleTrackingStep(step?.number);
  }, [step]);

  const handleDefaultLocalization = async () => {
    const defaultLang = "fr";

    try {
      await loadTranslations(defaultLang);
      i18n.changeLanguage(defaultLang);
      setIsTranslationsLoaded(true);
    } catch (error) {
      logger.error(`Error loading default translations: ${error}`);
    }
  };

  const handleLocalization = async (language: string) => {
    try {
      await loadTranslations(language);
      i18n.changeLanguage(language);
      setIsTranslationsLoaded(true);
    } catch (error) {
      await handleDefaultLocalization();
    }
  };

  useEffect(() => {
    if (isTranslationsLoaded) return;

    const language = initializeLanguage();

    handleLocalization(language);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTranslationsLoaded]);

  useEffect(() => {
    const localPid = userContext?.pid;
    const localUid = userContext?.uid;
    const localMid = userContext?.mid;

    if (localPid) setPid(localPid);
    if (localUid) setUid(localUid);
    if (localMid) setMid(localMid);
  }, [userContext]);

  const resolveLangRules = (lang?: string | null) => {
    if (isVelour) return LANGUAGE_RULES.default;

    const langBase = lang ? getLangBase(lang) : null;
    return (
      LANGUAGE_RULES[langBase as keyof typeof LANGUAGE_RULES] ??
      LANGUAGE_RULES.default
    );
  };

  useEffect(() => {
    const subdomain = getSubdomainPrefixUpper(domainData?.name || "");
    const validCountries = BASE_SIZES.map((size: any) => size.name);
    const countryCode = urlParameters.get("countryCode");
    const countryCodeAlt = urlParameters.get("country_code");
    const market = urlParameters.get("market");
    const marketParam = market || countryCode || countryCodeAlt;
    const imperialMarkets = ["US", "GB"];

    const resolveCountry = (): string => {
      if (lang === "fr") return "FR";

      if (subdomain && isLacoste) {
        if (subdomain === "GB") return "UK";
        if (validCountries.includes(subdomain)) return subdomain;
        return "EU";
      }

      return validCountries.includes(subdomain) ? subdomain : "EU";
    };

    const langBase = lang ? getLangBase(lang) : null;
    const langRules = resolveLangRules(langBase);

    const marketToBraSystem: Record<string, string> = {
      FR: "FR",
      GB: "UK",
      UK: "UK",
      US: "US",
      IT: "IT",
      JP: "JP",
      KR: "KO",
      AU: "AUS",
      ES: "FR",
      PT: "FR",
    };

    const resolvedBraSize = marketParam
      ? marketToBraSystem[marketParam.toUpperCase()] || "EU"
      : (domainData as any)?.defaultBraSize ||
        langRules.bra ||
        resolveCountry();

    const resolvedUnitSystem = marketParam
      ? imperialMarkets.includes(marketParam.toUpperCase())
        ? "imperial"
        : "metric"
      : langRules.unit || domainData?.unitSystem || "metric";

    setSizeCountry(isFootwear ? "EU" : resolvedBraSize);
    setUnit(resolvedUnitSystem === "imperial" ? "feet" : "cm");
    setUnitWeight(resolvedUnitSystem === "imperial" ? "lbs" : "kg");

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, domainData, brandDefined, lang, isFootwear]);

  useEffect(() => {
    const preventUnhandledRejection = (event: any) => {
      const reason = event.reason;

      if (!reason || typeof reason === "undefined") {
        event.preventDefault();
        return;
      }

      if (
        reason.message &&
        /Non-Error promise rejection/i.test(reason.message)
      ) {
        event.preventDefault();
      }
    };

    window.addEventListener("unhandledrejection", (event) =>
      preventUnhandledRejection(event),
    );

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isLingerieRestarting.current) return;
    if (
      !resultLoading.current &&
      uid &&
      pid &&
      !mock &&
      !outOfRange.current &&
      !(
        userContext?.midType.current === "scan" &&
        !userContext?.isScanChecked.current
      ) &&
      !(
        userContext?.midType.current === "question" &&
        !userContext?.isQuestionPushed.current
      )
    ) {
      logger.log("useEffect GTRS called");
      goToResultScreen(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    uid,
    pid,
    mid,
    productStockData,
    variantId,
    variantCol,
    mock,
    isFootwear,
    isChildren,
    isLingerie,
  ]);

  useEffect(() => {
    const cachedDomain = localStorage.getItem("domain");
    const currentDomain = getQueryParam("domain");

    if (currentDomain) {
      localStorage.setItem("domain", currentDomain);
    }

    if (mock) {
      if (userContext && currentDomain)
        userContext.createNewUser(currentDomain);

      return;
    }

    if (userContext) {
      if (currentDomain && cachedDomain !== currentDomain) {
        removeLocalStore("uid");
        removeLocalStore("mid");
        removeLocalStore("domain");
        userContext.createNewUser(currentDomain);
      } else if (!uid && userContext?.uid) {
        setUid(userContext?.uid);
      } else if (!uid && currentDomain) {
        userContext.createNewUser(currentDomain);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uid, userContext, uxGender, mock]);

  useEffect(() => {
    if (!step && !recommendedSize) {
      const actualStep = handleStep({ isMobile, step, recommendedSize });
      if (
        mock &&
        urlParameters.get("outOfRange") === "true" &&
        actualStep?.number === MODAL_STEPS.ERROR.number
      ) {
        outOfRange.current = true;
      }
      setDefaultStep(actualStep);
      setStep(actualStep);
    }

    if (isScanError && defaultStep) {
      setStep(defaultStep);
      setIsScanError(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, isFootwear, isScanError, defaultStep, recommendedSize]);

  useEffect(() => {
    if (!step) return;
    if (step.number === MODAL_STEPS.RESULT.number) return;

    const recalculatedStep = handleStep({ isMobile, step, recommendedSize });

    setStep(recalculatedStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isMobile, recommendedSize]);

  useEffect(() => {
    if (outOfRange.current) return;
    if (isLingerieRestarting.current) return;

    if (recommendedSize || shoeSelectedSize) {
      setStep(MODAL_STEPS.RESULT);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedSize, shoeSelectedSize]);

  const handleFieldChange = (e: any, type: string) => {
    const isCustomField =
      type === "age" || type === "feet" || type === "inches";
    const value = isCustomField ? e : e.target.value;

    switch (type) {
      case "height":
        setHeight(value);
        if (unit === "feet") {
          convertHeightToFeetAndInches(value);
        }
        break;
      case "feet":
        setFeet(value);
        convertFeetAndInchesToCm(value, inches);
        break;
      case "inches":
        setInches(value);
        convertFeetAndInchesToCm(feet, value);
        break;
      case "weight":
        setWeight(value);
        break;
      case "age":
        setAge(value);
        break;
      case "company":
        setCompany(value);
        break;
      case "year":
        setAgeChildrenYears(value);
        break;
      case "month":
        setAgeChildrenMonths(value);
        break;
      default:
        break;
    }
  };

  const handleUnitChange = (newUnit: string) => {
    if (newUnit !== null) {
      setUnit(newUnit);
      if (newUnit === "feet") {
        setUnitWeight("lbs");
        if (height) {
          const { feetValue, inchesValue } =
            convertHeightToFeetAndInches(height);
          setFeet(feetValue);
          setInches(inchesValue);
        }
      } else if (newUnit === "cm") {
        setUnitWeight("kg");
        if (feet || inches) {
          const heightStr = convertFeetAndInchesToCm(feet, inches);
          setHeight(heightStr);
        }
      }
    }
  };

  const handleUnitWeightChange = (newUnit: string) => {
    if (newUnit !== null) {
      setUnitWeight(newUnit);
      if (newUnit === "lbs") {
        setUnit("feet");
        if (height) {
          const { feetValue, inchesValue } =
            convertHeightToFeetAndInches(height);
          setFeet(feetValue);
          setInches(inchesValue);
        }
      } else if (newUnit === "kg") {
        setUnit("cm");
        if (feet || inches) {
          const heightStr = convertFeetAndInchesToCm(feet, inches);
          setHeight(heightStr);
        }
      }
    }
  };

  const handleUnitAgeChange = (newUnit: string) => {
    if (newUnit !== null) {
      setUnitAge(newUnit);
    }
  };

  useEffect(() => {
    if (isLingerie) {
      setSelectedGender(GENDERS.F);
      return;
    }
    if (!uxGender && localGender) {
      setSelectedGender(localGender);
      setSelectedShoeGender(localGender);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localGender]);

  const handleGenderSelect = (gender: string) => {
    if (!gender) return;
    if (gender === GENDERS.M || gender === GENDERS.F) {
      setSelectedGender(gender);
      setSelectedShoeGender(gender);
      localStorage.setItem("gender", gender);
    }
  };

  useEffect(() => {
    const data = recommendedSize || shoeSelectedSize;
    if (!data) return;

    const adjustedSize: RecommendedSizeType[] =
      isFootwear || isChildren
        ? (Object.values(data as ShoeRecommendationType).filter(
            Boolean,
          ) as RecommendedSizeType[])
        : (Object.values(data) as RecommendedSizeType[]);

    adjustedSize.sort(
      (a, b) => (a.label_rank ?? 0) - (b.label_rank ?? 0),
    );

    const idealSize: RecommendedSizeType | null =
      isFootwear || isChildren
        ? (data as ShoeRecommendationType).good || null
        : adjustedSize.find((s) => s.label_rank === 1) || null;

    const reducedResultRes: Record<number, RecommendedSizeType> =
      Object.fromEntries(adjustedSize.map((s, i) => [i, s]));

    const isSizeNotPossible = !idealSize?.possible;

    if (isSizeNotPossible) {
      outOfRange.current = true;
      setStep(MODAL_STEPS.ERROR);
    } else {
      outOfRange.current = false;
      setReducedResult(reducedResultRes);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recommendedSize, shoeSelectedSize, mock]);

  const fieldsValidation = () => {
    const newError = {
      height: { error: false, message: "" },
      weight: { error: false, message: "" },
      age: { error: false, message: "" },
      ageChildren: { error: false, message: "" },
      company: { error: false, message: "" },
    };

    const heightValue = parseInt(height, 10);
    const localFeet = feet ? Number(feet.split(" ")[0]) : null;
    const localInches = inches ? Number(inches.split(" ")[0]) : null;
    const localWeight = weight ? Number(weight.split(" ")[0]) : null;
    const localAge = age ? Number(age.split(" ")[0]) : null;

    const localAgeChildrenYears = ageChildrenYears
      ? Number(ageChildrenYears.split(" ")[0])
      : null;

    const localAgeChildrenMonths = ageChildrenMonths
      ? Number(ageChildrenMonths.split(" ")[0])
      : null;

    const rule = VALIDATION_RULES;

    if (
      unit === "cm" &&
      height !== "" &&
      ((heightValue < rule.height.min && !isChildren) ||
        heightValue > rule.height.max)
    ) {
      newError.height = { error: true, message: t("height.errorcm") };
    }

    if (unit === "feet" && inchesTouched && !feet && inches !== "") {
      newError.height = { error: true, message: t("height.errorfeet") };
    }

    if (unit === "feet" && feetTouched && inchesTouched) {
      const isFeetFilled = localFeet !== null;
      const isInchesFilled = localInches !== null;

      const isPartiallyFilled =
        (isFeetFilled && !isInchesFilled) || (!isFeetFilled && isInchesFilled);

      if (isPartiallyFilled) {
        newError.height = { error: true, message: t("height.errorfeet") };
        setError(newError);
        return true;
      }

      if (isFeetFilled && isInchesFilled) {
        const totalInches = localFeet * 12 + localInches;
        const minHeightInches =
          rule.height.feet.min * 12 + rule.height.feet.max;
        const maxHeightInches =
          rule.height.inches.min * 12 + rule.height.inches.max;

        if (
          (totalInches < minHeightInches && !isChildren) ||
          totalInches > maxHeightInches
        ) {
          newError.height = { error: true, message: t("height.errorfeet") };
          setError(newError);
          return true;
        }
      }
    }

    if (weight !== "") {
      if (
        (unitWeight === "kg" &&
          localWeight &&
          ((localWeight < rule.weight.kg.min && !isChildren) ||
            localWeight > rule.weight.kg.max)) ||
        localWeight === 0
      ) {
        newError.weight = { error: true, message: t("weight.errorkg") };
      } else if (
        unitWeight === "lbs" &&
        localWeight &&
        ((localWeight < rule.weight.lbs.min && !isChildren) ||
          localWeight > rule.weight.lbs.max)
      ) {
        newError.weight = { error: true, message: t("weight.errorlbs") };
      }
    }

    if (isChildren) {
      if (ageChildrenMonths !== "") {
        if (
          (localAgeChildrenMonths && localAgeChildrenMonths >= 24) ||
          localAgeChildrenMonths === 0
        ) {
          newError.ageChildren = { error: true, message: t("age.error") };
        }
      }

      if (ageChildrenYears !== "") {
        if (
          (localAgeChildrenYears && localAgeChildrenYears >= 18) ||
          localAgeChildrenYears === 0
        ) {
          newError.ageChildren = { error: true, message: t("age.error") };
        }
      }
    } else {
      if (age !== "") {
        if (
          (localAge && (localAge < rule.age.min || localAge > rule.age.max)) ||
          localAge === 0
        ) {
          newError.age = { error: true, message: t("age.error") };
        }
      }
    }

    if (isMarcyParis && touchedCompany) {
      const trimmedCompany = company.trim();
      const hasNumbers = /\d/.test(trimmedCompany);

      if (trimmedCompany === "" || hasNumbers) {
        newError.company = {
          error: true,
          message: t("company.error"),
        };
      }
    }

    setError(newError);

    return Object.values(newError).some((field) => field.error);
  };

  useEffect(() => {
    if (needValidate) fieldsValidation();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    needValidate,
    height,
    unit,
    weight,
    unitWeight,
    age,
    feet,
    inches,
    company,
    feetTouched,
    inchesTouched,
  ]);

  const handleGoBack = (currentStep: any | null) => {
    if (!currentStep) return;

    const { number } = currentStep;

    const mobileIntro = MODAL_STEPS.INTRO_MOBILE;
    const desktopIntro = MODAL_STEPS.INTRO;
    const defaultIntro = isMobile ? mobileIntro : desktopIntro;

    const backStepMap: Record<number, () => void> = {
      [MODAL_STEPS.DEVICE_SELECT.number]: () => {
        setStep(isMobile ? mobileIntro : desktopIntro);
      },
      [MODAL_STEPS.QR_CODE.number]: () => {
        if (isFootwear) {
          setStep(MODAL_STEPS.DEVICE_SELECT);
        } else if (
          !previousStep &&
          (uxRouteQuestionPushScan || uxRouteQuestionOnly)
        ) {
          setStep(defaultIntro);
        } else if (previousStep !== null) {
          setStep(previousStep);
        } else if (uxRouteDefault) {
          setStep(MODAL_STEPS.DEVICE_SELECT);
        }
      },
      [MODAL_STEPS.BELLY.number]: () => {
        if (uxRouteQuestionOnly || uxRoute === "") setStep(defaultIntro);
        else if (uxRouteDefault) setStep(MODAL_STEPS.DEVICE_SELECT);
      },
      [MODAL_STEPS.INTRO_MOBILE.number]: () => {
        if (!uxGender) setStep(MODAL_STEPS.GENDER);
      },
      [MODAL_STEPS.TORSO.number]: () => setStep(MODAL_STEPS.BELLY),
      [MODAL_STEPS.CUISSES.number]: () => setStep(MODAL_STEPS.TORSO),
      [MODAL_STEPS.SLEEVES.number]: () => setStep(MODAL_STEPS.CUISSES),
      [MODAL_STEPS.BREASTS.number]: () => {
        if (isFootwear) {
          setStep(
            !uxGender ? MODAL_STEPS.SHOE_GENDER : MODAL_STEPS.DEVICE_SELECT,
          );
        } else {
          setStep(
            uxRouteNone && selectedGender === GENDERS.F
              ? defaultStep
              : MODAL_STEPS.TORSO,
          );
        }
      },
      [MODAL_STEPS.UNAVAILABLE_RESULT.number]: restart,
      [MODAL_STEPS.RESULT.number]: restart,
      [MODAL_STEPS.ERROR.number]: restart,
      [MODAL_STEPS.SHOE_GENDER.number]: () =>
        setStep(MODAL_STEPS.DEVICE_SELECT),
      [MODAL_STEPS.SHOE_QUESTIONS.number]: () => setStep(MODAL_STEPS.BREASTS),
      [MODAL_STEPS.LINGERIE_QUESTION_1.number]: () =>
        setStep(MODAL_STEPS.BREASTS),
      [MODAL_STEPS.LINGERIE_QUESTION_2.number]: () =>
        setStep(MODAL_STEPS.LINGERIE_QUESTION_1),
      [MODAL_STEPS.LINGERIE_QUESTION_3.number]: () =>
        setStep(MODAL_STEPS.LINGERIE_QUESTION_2),
      [MODAL_STEPS.LINGERIE_QUESTION_4.number]: () =>
        setStep(MODAL_STEPS.LINGERIE_QUESTION_3),
      [MODAL_STEPS.LINGERIE_QUESTION_5.number]: () =>
        setStep(MODAL_STEPS.LINGERIE_QUESTION_4),
      [MODAL_STEPS.LINGERIE_QUESTION_6.number]: () =>
        setStep(MODAL_STEPS.LINGERIE_QUESTION_5),
    };

    if (backStepMap[number]) backStepMap[number]();

    handleAnalytics("action", "go_back", {
      key: "current_step",
      value: getStepByNumber(number),
    });

    setPreviousStep(null);
  };

  const computeDisableContinue = (): boolean => {
    const hasValidationErrors = Object.values(error).some(
      (field) => field.error,
    );
    const stableFieldsNotFound = !weight || !age || (!isLingerie && !selectedGender);

    if (isChildren) {
      const allFieldsNotFound =
        (unit === "feet" ? !feet || !inches : !height) ||
        !weight ||
        !selectedGender;
      const isAddedAllFields =
        unitAge === "month" ? !ageChildrenMonths : !ageChildrenYears;

      return hasValidationErrors || allFieldsNotFound || isAddedAllFields;
    } else {
      if (isMobile) {
        if (step === MODAL_STEPS.GENDER) {
          return !selectedGender;
        }
        if (step === MODAL_STEPS.INTRO_MOBILE) {
          return unit === "feet"
            ? !feet || !inches || hasValidationErrors || stableFieldsNotFound
            : !height || stableFieldsNotFound || hasValidationErrors;
        }
      } else {
        return unit === "feet"
          ? !feet || !inches || hasValidationErrors || stableFieldsNotFound
          : !height || stableFieldsNotFound || hasValidationErrors;
      }
    }

    return false;
  };

  useEffect(() => {
    let disabled = computeDisableContinue();

    if (isNinaRicci) {
      if (!policyChecked) {
        disabled = true;
      }
    }

    if (isMarcyParis) {
      if (company === "") {
        disabled = true;
      }
    }

    setDisableContinue(disabled);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    unit,
    selectedGender,
    feet,
    inches,
    height,
    weight,
    age,
    error,
    step,
    isMobile,
    policyChecked,
    company,
  ]);

  useEffect(() => {
    const handleMessage = async (event: any) => {
      try {
        let eventData = event?.data;
        const retailer = eventData?.retailer;
        if (eventData?.sizes?.length) {
          const sizesData = eventData?.sizes?.map((item: any) => ({
            variant_reference: item?.variantId,
            color: item?.color,
            quantity: item?.quantity,
          }));
          setProductStockData(sizesData);
          stocksRetrieved.current = true;
        } else {
          if (retailer === "asphalte") {
            logger.log("Event received from Asphalte", event);
            if (eventData?.url?.variant) {
              logger.log("Asphalte Variant received");
              setVariantId(eventData?.url?.variant);
            } else if (eventData?.url?.color) {
              setVariantCol(eventData?.url?.color);
            }
          }
        }
      } catch (e) {
        logger.warn("Message handler failed:", e);
      }
    };

    window.addEventListener("message", handleMessage);

    return () => {
      window.removeEventListener("message", handleMessage);
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const sendSizesLoop = async () => {
      for (let i = 0; i < 5; i++) {
        postGetSizes();
        if (i < 4) await new Promise((res) => setTimeout(res, 500));
      }
    };
    sendSizesLoop();
  }, []);

  const goToMockResultScreen = () => {
    if (isFootwear && updateSelectedSize) {
      updateSelectedSize(MOCK_FOOTWEAR_RECOMMENDATION);
    } else {
      setRecommendedSize(MOCK_ADULT_RECOMMENDATION);
    }

    if (!selectedGender && localGender) {
      setSelectedGender(localGender);
      setSelectedShoeGender(localGender);
    } else if (
      !selectedGender &&
      !localGender &&
      !localGender.includes(GENDERS.F)
    ) {
      setSelectedGender(GENDERS.M);
      localStorage.setItem("gender", GENDERS.M);
    }

    const mockStocksData = isFootwear
      ? MOCK_FOOTWEAR_STOCKS_DATA
      : MOCK_ADULT_STOCKS_DATA;

    setProductStockData(
      mockStocksData.map(
        (item: { variant_reference: string; quantity: number }) => {
          return {
            ...item,
            quantity: item.quantity,
          };
        },
      ),
    );

    if (isGivenchy || isDevernois || isGalore) {
      const mockSimilarProducts = isDevernois
        ? MOCK_SIMILAR_PRODUCTS_Devernois
        : isGalore
          ? MOCK_SIMILAR_PRODUCTS_Galore
          : MOCK_SIMILAR_PRODUCTS;
      setSimilarProducts(mockSimilarProducts);
    }
  };

  const goToResultScreen = async (isResultGenerated?: boolean) => {
    resultLoading.current = true;

    const storageMid = localStorage.getItem("mid") || null;

    if (pid && (storageMid || isLingerie)) {
      try {
        const result = isSpecificFlow
          ? await specificRecommend(pid, selectedSize?.variant_id)
          : await recommend(pid, mid, productStockData, variantId, variantCol);

        const normalizedResult = Array.isArray(result?.data)
          ? result?.data
          : result?.data
            ? [result?.data]
            : [];

        if (result?.success && normalizedResult.length > 0) {
          setRecommendedSize(result?.data);

          if (isFootwear && updateSelectedSize) {
            updateSelectedSize(result?.data);
          }

          if (!isLingerie) {
            if (!selectedGender && localGender) {
              setSelectedGender(localGender);
              setSelectedShoeGender(localGender);
            } else if (!selectedGender && !localGender) {
              setSelectedGender(GENDERS.M);
              setSelectedShoeGender(GENDERS.M);
              localStorage.setItem("gender", GENDERS.M);
            }
          }

          if (
            !resultScreenUI.generateResult &&
            !productStockData?.length &&
            !stocksRetrieved.current &&
            !isAsphalte &&
            !isSAAJ
          ) {
            if (domain && pid) {
              const stocksData = await checkStocks(
                domain,
                pid,
                brandDefined?.name,
              );
              if (stocksData !== null) {
                const resultStocksData = stocksData.map(
                  (item: { variant_reference: string; quantity: number }) => {
                    return {
                      ...item,
                      quantity: item.quantity,
                    };
                  },
                );

                logger.log(`stocksData: ${resultStocksData}`);
                if (resultStocksData.length > 0) {
                  setProductStockData(resultStocksData);
                }
              }
            }
          }
        } else {
          setStep(isResultGenerated ? MODAL_STEPS.ERROR : defaultStep);
          if (!uxGender) {
            setSelectedGender("");
            removeLocalStore("gender");
          }
        }
      } catch (error) {
        logger.error(`error:  ${error}`);
        setStep(MODAL_STEPS.ERROR);
      }
    } else {
      logger.warn("Recommendation uncomputable: no pid is found");
    }

    resultLoading.current = false;
  };

  useEffect(() => {
    const checkIsSizeUnavailable = (
      quantity: boolean | number | undefined,
    ): boolean => {
      if (typeof quantity === "boolean") {
        return !quantity;
      }
      if (typeof quantity === "number") {
        return quantity < 1;
      }
      return true;
    };

    if (mockSimilarProducts && recommendedSize?.length) {
      if (!productStockData?.length) {
        setProductStockData(
          recommendedSize.map((s: any) => ({
            variant_reference: s.variant_id,
            quantity: 0,
          })),
        );
      }
      setIsSizeUnavailable(true);
      if (!outOfRange.current) {
        setStep(MODAL_STEPS.UNAVAILABLE_RESULT);
      }
      return;
    }

    if (
      productStockData !== null &&
      recommendedSize !== null &&
      recommendedSize !== undefined
    ) {
      const findVariant = productStockData?.find(
        (item: any) =>
          item.variant_reference === selectedSize?.variant_id ||
          item.variant_reference === selectedSize?.reference ||
          (item.variant_reference === selectedSize?.label && isAntonelle),
      );

      if (findVariant) {
        const isSizeUnavailable = checkIsSizeUnavailable(findVariant?.quantity);
        setIsSizeUnavailable(isSizeUnavailable);
        if (!outOfRange.current) {
          setStep(
            isSizeUnavailable
              ? MODAL_STEPS.UNAVAILABLE_RESULT
              : MODAL_STEPS.RESULT,
          );
        } else {
          setStep(MODAL_STEPS.ERROR);
        }
      } else if (!resultScreenUI.generateResult) {
        setIsSizeUnavailable(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productStockData, selectedSize, recommendedSize]);

  const questionPushTriggerCall = async (): Promise<{
    success: boolean;
    status: number;
  }> => {
    let userId = localStorage.getItem("uid") ?? uid;

    if (!userId && userContext && domain) {
      const newUserId = await userContext.createNewUser(domain);
      if (!newUserId) return { success: false, status: 400 };
      userId = newUserId;
    }

    if (isFootwear && userId && selectedShoeGender !== null) {
      const result = await questionPushTrigger({
        user: {
          id: userId,
          answers: {
            usual_size: {
              value: sizeValue.current,
              system: sizeCountry,
            },
            foot_width: shoeQuestion ?? "standard",
            gender: selectedShoeGender,
          },
        },
      });

      return result;
    } else {
      return { success: false, status: 400 };
    }
  };

  const pushQuestionAnswersCall = async (
    payload: object,
    isSpecificProject?: boolean,
  ): Promise<{ success: boolean; status: number }> => {
    if (userContext) {
      const result = await userContext?.pushQuestionAnswers(
        payload,
        isSpecificProject,
      );

      return result;
    } else {
      return { success: false, status: 400 };
    }
  };

  const sendQuestionAnswers = async (isSkipResult?: boolean) => {
    const isMale = selectedGender === GENDERS.M;

    const heightLocal =
      unit === "feet" ? INCHtoCM(`${feet} ${inches}`) : height;
    const weightLocal =
      unitWeight === "lbs" && weight ? LBStoKG(parseInt(weight)) : weight;

    const commonData = {
      gender: isMale ? "male" : "female",
      age: age ? parseInt(age, 10) : 0,
      height: heightLocal ? parseInt(heightLocal, 10) : 0,
      weight: weightLocal ? parseInt(weightLocal, 10) : 0,
      sleeve_length: sleeveValue.current ?? null,
    };

    const defaultQuestions = isMale
      ? { male_belly: 2, male_hip: 2, male_chest: 2 }
      : {
          female_belly: 2,
          female_hip: 2,
          female_bra_size: isSkipResult ? null : sizeValue.current,
          female_bra_cup: isSkipResult ? null : cupValue.current,
        };

    const dynamicQuestions = isMale
      ? {
          male_belly: bellyValue.current || 2,
          male_hip: torsoValue.current || 2,
          male_chest: cuissesValue.current || 2,
        }
      : {
          female_belly: bellyValue.current || 2,
          female_hip: torsoValue.current || 2,
          female_bra_size: isSkipResult ? null : sizeValue.current,
          female_bra_cup: isSkipResult ? null : cupValue.current,
        };

    const data = {
      ...commonData,
      questions: uxRouteNone ? defaultQuestions : dynamicQuestions,
    };

    const ageChildren =
      unitAge === "year" ? ageChildrenYears : ageChildrenMonths;

    const childrenData = {
      gender: selectedGender,
      height: heightLocal ? parseInt(heightLocal, 10) : 0,
      weight: weightLocal ? parseInt(weightLocal, 10) : 0,
      age: {
        type: unitAge || "year",
        value: ageChildren ? parseInt(ageChildren, 10) : 0,
      },
    };

    const lingerieData = buildLingeriePayload(lingerieAnswers, {
      age: age ? parseInt(age, 10) : 0,
      height: heightLocal ? parseInt(heightLocal, 10) : 0,
      weight: weightLocal ? parseInt(weightLocal, 10) : 0,
      cup: cupValue.current,
      band: sizeValue.current,
    });

    const getPayload = () => {
      if (isChildren) return childrenData;
      if (isLingerie) return lingerieData;
      return data;
    };

    if (mock) {
      const mockOutOfRange = urlParameters.get("outOfRange") === "true";
      if (mockOutOfRange) {
        outOfRange.current = true;
        setStep(MODAL_STEPS.ERROR);
        return;
      }
      return goToMockResultScreen();
    }

    resultLoading.current = true;

    const payload = getPayload();
    const isSpecificProject = isChildren || isLingerie;

    const result: { success: boolean; status: number } = isFootwear
      ? await questionPushTriggerCall()
      : await pushQuestionAnswersCall(payload, isSpecificProject);

    resultLoading.current = false;

    if (result?.success && result?.status === 200) {
      isLingerieRestarting.current = false;
      await goToResultScreen(true);
    } else {
      logger.warn(
        `Skipping /recommend — trigger failed (status: ${result?.status})`,
      );
      setStep(MODAL_STEPS.ERROR);
    }

    return result;
  };

  const restart = () => {
    setAgeChildrenYears("");
    setAgeChildrenMonths("");
    setShoeQuestion(null);
    setHeight("");
    setFeet("");
    setInches("");
    setWeight("");
    setAge("");
    if (!uxGender) {
      setSelectedGender("");
      setSelectedShoeGender(null);
      removeLocalStore("gender");
    }
    setDisableContinue(true);
    bellyValue.current = 0;
    torsoValue.current = 0;
    cuissesValue.current = 0;
    sizeValue.current = "";
    cupValue.current = "";
    outOfRange.current = false;
    setRecommendedSize(null);
    setReducedResult(null);
    if (isFootwear) {
      sessionStorage.setItem("shoe_retry", Date.now().toString());
    }
    setLingerieAnswers(emptyLingerieAnswers());
    setSelectedSize(null);
    if (updateSelectedSize) updateSelectedSize(null);

    if (isLingerie) isLingerieRestarting.current = true;
    setStep(defaultStep);
  };

  useEffect(() => {
    return () => restart();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const closeIframe = () => {
    postCloseIframe();

    if (step && step?.number) {
      handleAnalytics("action", "exit", null);
    }
  };

  const handleOverlayClick = (e: any) => {
    if (popupRef.current && !popupRef.current.contains(e.target)) {
      closeIframe();
    }
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        closeIframe();
      }
    };

    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userContext, step]);

  useEffect(() => {
    document.documentElement.lang = lang || "fr";
  }, [lang]);

  useEffect(() => {
    if (isMobile) {
      let rafId: number;
      const setDrawerHeight = () => {
        cancelAnimationFrame(rafId);
        rafId = requestAnimationFrame(() => {
          const vh = window.innerHeight * 0.01;
          document.documentElement.style.setProperty("--vh", `${vh}px`);
        });
      };

      setDrawerHeight();
      window.addEventListener("resize", setDrawerHeight);

      return () => {
        cancelAnimationFrame(rafId);
        window.removeEventListener("resize", setDrawerHeight);
      };
    }
  }, [isMobile]);

  return (
    <Box
      className="drawer-overlay"
      role="dialog"
      aria-modal="true"
      lang={lang || "fr"}
      onKeyDown={(e) => {
        if (e.key === "Escape") handleOverlayClick(e);
      }}
      onClick={(e: any) => {
        const target = e.target as HTMLElement;

        if (["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName)) {
          return;
        }

        if (e.target === e.currentTarget) {
          handleOverlayClick(e);
        }
      }}
      style={{
        fontFamily: isMobile
          ? `${font}, sans-serif`
          : `${font}, sans-serif !important`,
        backgroundColor: isGivenchy
          ? "rgba(245, 246, 247, 0.8)"
          : "rgba(0, 0, 0, 0.5)",
        ["--focus-color" as string]: isBlackstore ? "#FFDD00" : "#036ded",
      }}
    >
      <Box
        className="drawer-popup"
        ref={popupRef}
        style={popupStyles}
        tabIndex={-1}
        onClick={(e) => {
          if (isMobile) e.stopPropagation();
        }}
      >
        <Box>
          {isTranslationsLoaded ? (
            <>
              <Box className="drawer-popup-header" style={headerStyles}>
                <BackArrowButton
                  {...{
                    step,
                    brandName: brandDefined?.name,
                    handleGoBack,
                  }}
                />
                {isSRP && <SrpTitleGuide />}
                {isVB && (
                  <VictoriaBeckhamGuide
                    step={
                      step ?? { number: getStepMaps(isMobile)[0].stepNumber }
                    }
                    stepMap={getStepMaps(isMobile)}
                  />
                )}
                <CloseIconButton
                  {...{
                    step,
                    brandName: brandDefined?.name,
                    titleStyles,
                    closeIframe,
                  }}
                />
              </Box>
              <Box
                className="drawer-popup-content"
                style={{
                  backgroundColor: isSRP ? "#F7F7F7" : "#FFFFFF",
                  padding: isGivenchy && !isMobile ? "100px 40px 0 40px" : 0,
                }}
              >
                <StepTitle
                  {...{
                    step,
                    titleTextTransform,
                    titleStyles,
                    outOfRange: outOfRange.current,
                    MODAL_STEPS_TITLE,
                    OUT_OF_RANGE_TITLE,
                    handleGoBack,
                  }}
                />
                <DrawerSteps
                  step={step}
                  defaultStep={defaultStep}
                  error={error}
                  height={height}
                  unit={unit}
                  feet={feet}
                  inches={inches}
                  unitWeight={unitWeight}
                  weight={weight}
                  age={age}
                  ageChildrenYears={ageChildrenYears}
                  ageChildrenMonths={ageChildrenMonths}
                  unitAge={unitAge}
                  handleUnitAgeChange={handleUnitAgeChange}
                  selectedGender={selectedGender}
                  selectedShoeGender={selectedShoeGender}
                  setSelectedShoeGender={setSelectedShoeGender}
                  disableContinue={disableContinue}
                  bellyValue={bellyValue}
                  torsoValue={torsoValue}
                  cuissesValue={cuissesValue}
                  sizeValue={sizeValue}
                  cupValue={cupValue}
                  sleeveValue={sleeveValue}
                  sendQuestionAnswers={sendQuestionAnswers}
                  handleFieldChange={handleFieldChange}
                  handleUnitChange={handleUnitChange}
                  handleUnitWeightChange={handleUnitWeightChange}
                  handleGenderSelect={handleGenderSelect}
                  setNeedValidate={setNeedValidate}
                  recommendedSize={recommendedSize || shoeSelectedSize}
                  setRecommendedSize={setRecommendedSize}
                  reducedResult={reducedResult}
                  selectedRoute={selectedRoute}
                  setSelectedRoute={setSelectedRoute}
                  sizeCountry={sizeCountry}
                  setSizeCountry={setSizeCountry}
                  productStockData={productStockData}
                  variantId={variantId}
                  variantCol={variantCol}
                  similarProducts={similarProducts}
                  isSizeUnavailable={
                    isSizeUnavailable || selectedSize?.variant_id === null
                  }
                  selectedSize={selectedSize}
                  setSelectedSize={setSelectedSize}
                  setStep={setStep}
                  setPreviousStep={setPreviousStep}
                  restart={restart}
                  setSimilarProducts={setSimilarProducts}
                  isChildren={isChildren}
                  setIsScanError={setIsScanError}
                  outOfRange={outOfRange.current}
                  shoeQuestion={shoeQuestion}
                  setShoeQuestion={setShoeQuestion}
                  policyChecked={policyChecked}
                  setPolicyChecked={setPolicyChecked}
                  lingerieAnswers={lingerieAnswers}
                  setLingerieAnswer={setLingerieAnswer}
                  titlesStyles={titlesStyles}
                  descriptionStyles={descriptionStyles}
                  company={company}
                  fieldsValidation={fieldsValidation}
                  setDisableContinue={setDisableContinue}
                  setTouchedCompany={setTouchedCompany}
                  setFeetTouched={setFeetTouched}
                  setInchesTouched={setInchesTouched}
                />
              </Box>
            </>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default DrawerModal;
